import { createRoute, useLocation } from '@tanstack/react-router'
import { projectRoute } from '../../route'
import { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { usePanelRef } from 'react-resizable-panels'
import { PanelRightOpen } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  BROWSER_TOOL_NAMES,
  isChatMode,
  type ChatMode,
  type ChatAttachment,
  type SimulateErrorKind,
} from '@hub/contracts'
import { ChatComposer } from '@/components/chat/chat-composer'
import { SelectionActionMenu } from '@/components/chat/selection-menu'
import { ChatThread } from '@/components/chat/chat-thread'
import { OnboardingBanner } from '@/components/chat/onboarding-banner'
import { WorkspaceSidebar } from '@/components/chat/workspace-sidebar'
import { collectWorkspaceItems } from '@/lib/chat/workspace-items'
import { JumpToLatest } from '@/components/chat/jump-to-latest'
import { DevMenu } from '@/components/chat/dev-menu'
import { ghostButton } from '@/components/assistant-ui/elements/surfaces'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useChatCollab } from '@/lib/collab/use-chat-collab'
import { useStickToBottom } from '@/lib/chat/stick-to-bottom'
import { orpc, creditsExhaustedReason } from '@/lib/orpc-client'
import { toast } from 'sonner'
import { getChatDraftMode, restoreChatDraft } from '@/lib/chat-draft-store'
import { useAuthState } from '@/lib/auth-state'
import { useIsMobile } from '@/hooks/use-mobile'
import { useSetPageSubtitle } from '@/lib/page-title-context'
import { cn } from '@/lib/utils'

const WORKSPACE_KEY = 'hub-chat-workspace-open'
/** Panel share (percent) with and without the shared browser (PLAN-083). */
const WORKSPACE_MAX_WITH_BROWSER = 65

function readWorkspaceOpen(): boolean {
  try {
    return localStorage.getItem(WORKSPACE_KEY) === '1'
  } catch {
    return false
  }
}

export const chatDetailRoute = createRoute({
  getParentRoute: () => projectRoute,
  path: '/chat/$chatId',
  component: ChatPage,
})

function ChatPage() {
  const { t } = useTranslation('chat')
  const { projectId, chatId } = chatDetailRoute.useParams()
  const {
    messages,
    sendMessage,
    discardMessage,
    deleteMessage,
    softDeleteMessage,
    clearAgentError,
    stopAgent,
    typingUsers,
    setTyping,
    synced,
    ready,
    agentStatus,
    agentBusySince,
    messageQueue,
    enqueueMessage,
    removeQueuedMessage,
    drainQueue,
    hasOlderMessages,
    loadingOlder,
    loadOlderMessages,
  } = useChatCollab(projectId, chatId)
  const [title, setTitle] = useState<string | undefined>()
  // Board 5q82: the onboarding thread gets a banner and a Retry for its first turn.
  const [threadMeta, setThreadMeta] = useState<{
    threadId: string
    onboarding: boolean
    archived: boolean
  } | null>(null)
  const onboarding = threadMeta?.threadId === chatId && threadMeta.onboarding
  const archived = threadMeta?.threadId === chatId && threadMeta.archived
  const [simulateError, setSimulateError] = useState<SimulateErrorKind | undefined>()
  const [workspaceOpen, setWorkspaceOpen] = useState(readWorkspaceOpen)
  const isMobile = useIsMobile()
  // PLAN-083: the thread has driven the shared browser → live view in the panel.
  const browserActive = messages.some((m) =>
    m.steps?.some((s) => s.kind === 'tool' && BROWSER_TOOL_NAMES.has(s.toolName))
  )
  // The panel (and its toggle) only exist when there is something to show.
  const workspaceItemCount = useMemo(() => collectWorkspaceItems(messages).count, [messages])
  const hasWorkspaceContent = browserActive || workspaceItemCount > 0
  // The workspace panel is desktop-only for now.
  const showWorkspace = workspaceOpen && !isMobile && hasWorkspaceContent
  // A browser opens the panel and grows it to its maximum the first time.
  const workspacePanelRef = usePanelRef()
  const browserSeen = useRef(false)
  useEffect(() => {
    if (browserActive && !browserSeen.current) {
      browserSeen.current = true
      setWorkspaceOpen(true)
    }
  }, [browserActive])
  useEffect(() => {
    if (browserActive && showWorkspace)
      workspacePanelRef.current?.resize(`${WORKSPACE_MAX_WITH_BROWSER}`)
    // Only when the browser appears or the panel mounts with one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browserActive, showWorkspace])
  const [retryingMessageId, setRetryingMessageId] = useState<string | null>(null)
  const { me } = useAuthState()
  // Dev-only affordance — gate by hostname; the simulateError flag is also
  // stripped server-side in production, so this is purely a UI clutter guard.
  const isLocalDev =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1' ||
      window.location.hostname === '[::1]')
  const showSimulator = isLocalDev && me?.isSuperAdmin
  const scrollRootRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const location = useLocation()
  const initialMessageRef = useRef<string | null>(
    (location.state as unknown as Record<string, unknown> | null)?.initialMessage as string | null
  )
  const initialAttachmentsRef = useRef<ChatAttachment[] | null>(
    (location.state as unknown as Record<string, unknown> | null)?.initialAttachments as
      ChatAttachment[] | null
  )
  const initialModeRef = useRef<ChatMode | null>(
    ((location.state as unknown as Record<string, unknown> | null)?.initialMode as ChatMode) ?? null
  )
  const processingQueueRef = useRef(false)

  const toggleWorkspace = useCallback(() => {
    setWorkspaceOpen((open) => {
      try {
        localStorage.setItem(WORKSPACE_KEY, open ? '0' : '1')
      } catch {
        /* ignore */
      }
      return !open
    })
  }, [])

  const handleRename = useCallback(
    async (newName: string) => {
      await orpc.chat.updateThread({ projectId, threadId: chatId, title: newName })
      setTitle(newName)
      window.dispatchEvent(new CustomEvent('chat-threads-changed'))
    },
    [projectId, chatId]
  )

  useSetPageSubtitle(title ?? null, handleRename)

  const triggerAgent = useCallback(
    async (content: string, attachments?: ChatAttachment[], messageId?: string) => {
      const attachmentMeta = attachments?.map((a) => ({
        id: a.id,
        filename: a.agentFilename ?? a.filename,
        url: a.url,
        size: a.size,
        contentType: a.contentType,
        ...(a.documentId ? { documentId: a.documentId } : {}),
      }))
      const draftMode = getChatDraftMode(projectId, chatId)
      const mode = initialModeRef.current ?? (isChatMode(draftMode) ? draftMode : 'normal')
      initialModeRef.current = null
      try {
        await orpc.chat.triggerAgent({
          mode,
          projectId,
          threadId: chatId,
          content,
          messageId,
          attachments: attachmentMeta,
          ...(simulateError ? { simulateError } : {}),
        })
      } catch (err) {
        if (messageId) discardMessage(messageId)
        throw err
      }
      if (simulateError) setSimulateError(undefined)
    },
    [projectId, chatId, simulateError, discardMessage]
  )

  /**
   * Every trigger failure funnels through here: an exhausted budget gets the
   * translated notice (the message echo was already discarded), anything else
   * goes to the console as before. Returns true when it was a credits refusal.
   */
  const reportTriggerFailure = useCallback(
    (err: unknown): boolean => {
      const reason = creditsExhaustedReason(err)
      if (reason) {
        toast.error(t(reason === 'project' ? 'credits.exhaustedProject' : 'credits.exhaustedOrg'))
        return true
      }
      console.error('Failed to trigger agent:', err)
      return false
    },
    [t]
  )

  // Handle initial message from new-chat navigation (skip if already sent, e.g. on refresh)
  useEffect(() => {
    if (synced && initialMessageRef.current && messages.length === 0) {
      const msg = initialMessageRef.current
      const attachments = initialAttachmentsRef.current ?? undefined
      initialMessageRef.current = null
      initialAttachmentsRef.current = null
      window.history.replaceState({}, '')
      const id = sendMessage(msg, attachments)
      triggerAgent(msg, attachments, id).catch((err) => {
        // The echo is gone; put the text back so the composer re-hydrates it.
        if (reportTriggerFailure(err)) restoreChatDraft(projectId, chatId, msg)
      })
    }
  }, [synced, messages.length, sendMessage, triggerAgent, reportTriggerFailure, projectId, chatId])

  const prevAgentStateRef = useRef(agentStatus.state)

  const loadThreadMeta = useCallback(
    (thread: { id: string; onboarding?: boolean; archivedAt?: string | null }) =>
      setThreadMeta({
        threadId: thread.id,
        onboarding: thread.onboarding === true,
        archived: !!thread.archivedAt,
      }),
    []
  )

  // Fetch thread metadata (title) via REST
  useEffect(() => {
    orpc.chat
      .getThread({ projectId, threadId: chatId })
      .then((res) => {
        setTitle(res.thread.title ?? undefined)
        loadThreadMeta(res.thread)
      })
      .catch(() => {})
  }, [projectId, chatId, loadThreadMeta])

  // Re-fetch title when agent finishes; clear the retry spinner.
  useEffect(() => {
    const prev = prevAgentStateRef.current
    prevAgentStateRef.current = agentStatus.state
    if (prev !== 'idle' && (agentStatus.state === 'idle' || agentStatus.state === 'error')) {
      setRetryingMessageId(null)
      orpc.chat
        .getThread({ projectId, threadId: chatId })
        .then((res) => {
          loadThreadMeta(res.thread)
          if (res.thread.title) {
            setTitle(res.thread.title)
            window.dispatchEvent(new CustomEvent('chat-threads-changed'))
          }
        })
        .catch(() => {})
    }
  }, [agentStatus.state, projectId, chatId, loadThreadMeta])

  // 'error' is a terminal state — the agent isn't doing work anymore, so it's
  // safe to send/drain from there.
  const isAgentBusy = agentStatus.state !== 'idle' && agentStatus.state !== 'error'

  // Stick to bottom while content grows; while the agent streams, only let go
  // once the reader has scrolled up past their own latest message.
  const { pinned, newBelow, scrollToBottom } = useStickToBottom({
    rootRef: scrollRootRef,
    viewportSelector: '[data-radix-scroll-area-viewport]',
    contentRef,
    anchorSelector: '[data-slot="user-message"]',
    streaming: isAgentBusy,
    resetKey: `${chatId}:${ready}`,
  })

  // When continuing from an error state, reset the room status first. The
  // failed assistant message stays in the thread (its error card is the
  // record of what happened); retry replaces it explicitly.
  const cleanupAfterError = useCallback(() => {
    if (agentStatus.state === 'error') clearAgentError()
  }, [agentStatus.state, clearAgentError])

  // Process queue when agent goes idle — drain all queued messages into one
  useEffect(() => {
    if (isAgentBusy || messageQueue.length === 0) return
    if (processingQueueRef.current) return

    processingQueueRef.current = true
    cleanupAfterError()
    const items = drainQueue()
    if (items.length === 0) {
      processingQueueRef.current = false
      return
    }

    const mergedContent = items.map((item) => item.content).join('\n\n')
    const mergedAttachments = items.flatMap((item) => item.attachments ?? [])

    const merged = mergedAttachments.length > 0 ? mergedAttachments : undefined
    const id = sendMessage(mergedContent, merged)
    triggerAgent(mergedContent, merged, id)
      .catch((err) => {
        if (reportTriggerFailure(err)) restoreChatDraft(projectId, chatId, mergedContent)
      })
      .finally(() => {
        processingQueueRef.current = false
      })
  }, [
    isAgentBusy,
    messageQueue.length,
    drainQueue,
    sendMessage,
    triggerAgent,
    cleanupAfterError,
    reportTriggerFailure,
    projectId,
    chatId,
  ])

  // messagesRef keeps handleRetry referentially stable.
  const messagesRef = useRef(messages)
  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  const handleRetry = useCallback(
    (messageId: string) => {
      const list = messagesRef.current
      const idx = list.findIndex((m) => m.id === messageId)
      if (idx === -1) return
      let prev: (typeof list)[number] | undefined
      for (let i = idx - 1; i >= 0; i--) {
        if (list[i].role === 'user') {
          prev = list[i]
          break
        }
      }
      if (!prev) {
        // Board 5q82: HUB's opening turn of the onboarding failed — there is
        // no user message to re-send, so ask the server for the kickoff again.
        if (!onboarding) return
        setRetryingMessageId(messageId)
        clearAgentError()
        orpc.chat
          .startOnboarding({ projectId, retry: true })
          .then(() => deleteMessage(messageId))
          .catch((err) => {
            setRetryingMessageId(null)
            reportTriggerFailure(err)
          })
        return
      }
      const previous = prev
      setRetryingMessageId(messageId)
      clearAgentError()
      // Same id → the store keeps the existing user row instead of appending.
      // The failed assistant message is only dropped once the new turn was
      // accepted: a refusal (out of credits) leaves the thread untouched, so
      // the error card and its Retry button are still there afterwards.
      triggerAgent(previous.content, previous.attachments, previous.id)
        .then(() => deleteMessage(messageId))
        .catch((err) => {
          setRetryingMessageId(null)
          reportTriggerFailure(err)
        })
    },
    [
      deleteMessage,
      clearAgentError,
      triggerAgent,
      reportTriggerFailure,
      setRetryingMessageId,
      onboarding,
      projectId,
    ]
  )

  const handleSendMessage = async (message: string, attachments?: ChatAttachment[]) => {
    setTyping(false)

    if (isAgentBusy) {
      enqueueMessage(message, attachments)
      return
    }

    cleanupAfterError()
    const id = sendMessage(message, attachments)
    scrollToBottom()
    try {
      await triggerAgent(message, attachments, id)
    } catch (err) {
      // The echo was already discarded by triggerAgent; returning false makes
      // the composer put the draft (and its attachments) back.
      if (reportTriggerFailure(err)) return false
    }
  }

  const thread = (
    <div className="relative -mt-(--topbar-h) flex h-full flex-1 flex-col overflow-hidden">
      {!showWorkspace && hasWorkspaceContent && (
        <button
          type="button"
          aria-label={t('thread.openWorkspace')}
          onClick={toggleWorkspace}
          className={cn(
            ghostButton,
            'absolute top-[calc(var(--topbar-h)+0.75rem)] right-3 z-20 size-8 max-md:hidden phone-landscape:hidden'
          )}
        >
          <PanelRightOpen className="size-4" />
        </button>
      )}
      <div ref={scrollRootRef} className="relative flex min-h-0 flex-1 flex-col">
        <ScrollArea className="flex-1">
          {/* Top padding clears the fade band; bottom = floating composer + fade. */}
          <div
            ref={contentRef}
            className="mx-auto max-w-3xl px-4 pt-[calc(var(--topbar-h)+1.5rem)] pb-44"
          >
            {onboarding && (
              <OnboardingBanner
                projectId={projectId}
                threadId={chatId}
                archived={archived}
                onRestored={() =>
                  setThreadMeta({ threadId: chatId, onboarding: true, archived: false })
                }
              />
            )}
            {!ready ? (
              <ThreadSkeleton label={t('thread.syncing')} />
            ) : (
              <ChatThread
                messages={messages}
                projectId={projectId}
                currentUserId={me?.user.id}
                agentStatus={agentStatus}
                messageQueue={messageQueue}
                typingUsers={typingUsers}
                hasOlderMessages={hasOlderMessages}
                loadingOlder={loadingOlder}
                onLoadOlder={loadOlderMessages}
                onRetry={handleRetry}
                retryFirstTurn={onboarding}
                retryingMessageId={retryingMessageId}
                onDeleteOwnMessage={softDeleteMessage}
                onRemoveQueued={removeQueuedMessage}
              />
            )}
          </div>
        </ScrollArea>

        {/* Floating composer — the thread scrolls underneath and fades out. */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10">
          <div
            aria-hidden
            className="absolute inset-x-0 -top-10 h-10 backdrop-blur-[3px]"
            style={{ maskImage: 'linear-gradient(to top, black 35%, transparent)' }}
          />
          <div
            aria-hidden
            className="from-background via-background/60 absolute inset-x-0 -top-10 h-10 bg-gradient-to-t to-transparent"
          />
          <div className="bg-background pointer-events-auto relative mx-auto flex max-w-3xl flex-col items-center gap-1.5 px-4 pb-2.5">
            {!pinned && (
              <div className="pointer-events-none absolute -top-12 inset-x-0 flex justify-center">
                <JumpToLatest
                  hasNew={newBelow}
                  onClick={scrollToBottom}
                  className="pointer-events-auto"
                />
              </div>
            )}
            <SelectionActionMenu draftKey={{ projectId, threadKey: chatId }} />
            <ChatComposer
              projectId={projectId}
              onSend={handleSendMessage}
              onTyping={setTyping}
              busy={isAgentBusy}
              onStop={stopAgent}
              busySince={agentBusySince}
              draftKey={{ projectId, threadKey: chatId }}
              className="w-full"
            />
            <div className="relative flex w-full items-center justify-center">
              {showSimulator && (
                <DevMenu
                  mode={simulateError}
                  onModeChange={setSimulateError}
                  className="absolute left-0"
                />
              )}
              <p className="text-foreground/40 text-[11px]">{t('thread.disclaimer')}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )

  // The panel group is always rendered, with or without the workspace: React
  // keeps the thread subtree mounted only as long as its position in the tree
  // keeps the same shape. Returning a bare `thread` when the panel is closed
  // would unmount and remount it on every toggle — a fresh ScrollArea viewport
  // at scrollTop 0 (the reader thrown to the top of the thread), with
  // `useStickToBottom` left observing the detached one.
  return (
    // react-resizable-panels v4: numbers are pixels, unit-less strings are percent.
    <ResizablePanelGroup orientation="horizontal" className="-mt-(--topbar-h) flex-1">
      <ResizablePanel
        defaultSize={browserActive ? `${100 - WORKSPACE_MAX_WITH_BROWSER}` : '70'}
        minSize="35"
        className="flex min-w-0 flex-col pt-(--topbar-h)"
      >
        {thread}
      </ResizablePanel>
      {showWorkspace && (
        <>
          <ResizableHandle withHandle />
          <ResizablePanel
            panelRef={workspacePanelRef}
            defaultSize={browserActive ? `${WORKSPACE_MAX_WITH_BROWSER}` : '30'}
            minSize="20"
            maxSize={browserActive ? `${WORKSPACE_MAX_WITH_BROWSER}` : '45'}
            className="min-w-0 pt-(--topbar-h)"
          >
            <WorkspaceSidebar
              projectId={projectId}
              threadId={chatId}
              messages={messages}
              browserActive={browserActive}
              onClose={toggleWorkspace}
            />
          </ResizablePanel>
        </>
      )}
    </ResizablePanelGroup>
  )
}

/**
 * Placeholder while the room has neither a cached copy nor a server sync —
 * the shell (header, composer) renders immediately so navigation feels
 * instant; only the message column waits.
 */
function ThreadSkeleton({ label }: { label: string }) {
  return (
    <div role="status" aria-live="polite" aria-label={label} className="flex flex-col gap-6 pt-2">
      <span className="sr-only">{label}</span>
      <div className="flex justify-end">
        <div className="bg-muted h-9 w-40 animate-pulse rounded-2xl" />
      </div>
      <div className="flex flex-col gap-2">
        <div className="bg-muted h-3 w-24 animate-pulse rounded" />
        <div className="bg-muted h-4 w-3/4 animate-pulse rounded" />
        <div className="bg-muted h-4 w-1/2 animate-pulse rounded" />
      </div>
      <div className="flex justify-end">
        <div className="bg-muted h-9 w-56 animate-pulse rounded-2xl" />
      </div>
      <div className="flex flex-col gap-2">
        <div className="bg-muted h-3 w-24 animate-pulse rounded" />
        <div className="bg-muted h-4 w-2/3 animate-pulse rounded" />
      </div>
    </div>
  )
}
